import type { ScopeSectionSchema } from './types'

export const generalSections: ScopeSectionSchema[] = [
  {
    id: 'general_pdf_export',
    tabId: 'general',
    formTitle: 'General',
    pdfTitle: 'General',
    pdfDescription: 'General scope and implementation details.',
    highLevelSummary: 'General scope and timeline details.',
    groups: [
      {
        id: 'general_timeline_volume',
        tabId: 'general',
        sectionId: 'general_pdf_export',
        formTitle: 'Timeline & Volume',
        pdfTitle: 'Timeline & Volume',
        pdfDescription: 'Timeline and volume configuration.',
      },
      {
        id: 'general_corporate_structure',
        tabId: 'advanced',
        sectionId: 'general_pdf_export',
        formTitle: 'Corporate Structure',
        pdfTitle: 'Corporate Structure',
        pdfDescription: 'Corporate structure and reseller settings.',
      },
      {
        id: 'general_technical_setup',
        tabId: 'general',
        sectionId: 'general_pdf_export',
        formTitle: 'Technical Setup',
        pdfTitle: 'Technical Setup',
        pdfDescription: 'Technical setup and access requirements.',
      },
      {
        id: 'general_portal_access_users',
        tabId: 'general',
        sectionId: 'general_pdf_export',
        formTitle: 'Portal Access Users',
        formDescription: 'Users who will be added as Administrators and can add other users and assign roles.',
        pdfTitle: 'Portal Access Users',
        pdfDescription: 'Portal access users summary.',
      },
      {
        id: 'general_phases_group',
        tabId: 'general',
        sectionId: 'general_pdf_export',
        formTitle: 'Phases',
        pdfTitle: 'Phases',
        pdfDescription: 'Phase scope descriptions.',
      },
      {
        id: 'general_sow_variation_group',
        tabId: 'advanced',
        sectionId: 'general_pdf_export',
        formTitle: 'SOW Variation',
        pdfTitle: 'SOW Variation',
        pdfDescription: 'Scope variation note.',
      },
      {
        id: 'general_testing_group',
        tabId: 'advanced',
        sectionId: 'general_pdf_export',
        formTitle: 'Testing',
        pdfTitle: 'Testing',
        pdfDescription: 'Testing requirements and preferences.',
      },
      {
        id: 'general_custom_works_group',
        tabId: 'custom_work',
        sectionId: 'general_pdf_export',
        formTitle: 'Custom Works',
        pdfTitle: 'Custom Works',
        pdfDescription: 'Custom works included for transparency on agreed non-standard changes.',
      },
    ],
    fields: [
      {
        id: 'general_client_use_case',
        tabId: 'general',
        sectionId: 'general_pdf_export',
        path: 'generalInfo.clientUseCase',
        valueType: 'textarea',
        formLabel: 'Client use case',
        formDescription: 'Required for completion. Up to 500 words. Keep it outcome-focused (what, why, and success criteria).',
        pdfLabel: 'Client use case',
        pdfDescription: 'Provide a short outcome-focused summary of the client use case.',
        contributesToCompletion: false,
      },
      {
        id: 'general_reseller_agreement',
        tabId: 'advanced',
        sectionId: 'general_pdf_export',
        groupId: 'general_corporate_structure',
        path: 'generalInfo.resellerAgreement',
        valueType: 'tri_state',
        formLabel: 'Reseller agreement',
        formDescription: 'Required for completion. Select if this deal is part of a reseller agreement.',
        pdfLabel: 'Reseller agreement',
        pdfDescription: 'Confirm whether this deal is part of a reseller agreement.',
        contributesToCompletion: false,
      },
      {
        id: 'general_reseller_name',
        tabId: 'advanced',
        sectionId: 'general_pdf_export',
        groupId: 'general_corporate_structure',
        path: 'generalInfo.resellerName',
        valueType: 'text',
        formLabel: 'Reseller name',
        formDescription: 'Required for completion (when Reseller agreement is Yes). Select the reseller associated with this agreement.',
        pdfLabel: 'Reseller name',
        pdfDescription: 'Select which reseller this deal is associated with.',
        contributesToCompletion: false,
      },
      {
        id: 'general_reseller_relationship_type',
        tabId: 'advanced',
        sectionId: 'general_pdf_export',
        groupId: 'general_corporate_structure',
        path: 'generalInfo.resellerRelationshipType',
        valueType: 'select',
        options: [
          { value: 'unknown', label: "Don't Know" },
          { value: 'selling_to_customers', label: 'This scope is for the reseller selling to their customers' },
          { value: 'customer_of_reseller', label: 'This scope is for a customer of a reseller' },
        ],
        formLabel: 'Reseller relationship',
        formDescription: 'Clarify whether this scope is for the reseller themselves or for one of their customers.',
        pdfLabel: 'Reseller relationship',
        pdfDescription: 'Clarifies whether this scope belongs to the reseller or to a customer of the reseller.',
        contributesToCompletion: false,
      },
      {
        id: 'general_reseller_predefined_config',
        tabId: 'advanced',
        sectionId: 'general_pdf_export',
        groupId: 'general_corporate_structure',
        path: 'generalInfo.resellerPredefinedConfig',
        valueType: 'tri_state',
        formLabel: 'Predefined configuration',
        formDescription: 'Does the reseller agreement involve the customer selling a predefined configuration to their customers?',
        pdfLabel: 'Predefined configuration',
        pdfDescription: 'Indicates whether the reseller agreement uses a predefined configuration sold to downstream customers.',
        contributesToCompletion: false,
      },
      {
        id: 'general_billing_requirements',
        tabId: 'advanced',
        sectionId: 'general_pdf_export',
        groupId: 'general_billing_group',
        path: 'generalInfo.billingRequirements',
        valueType: 'textarea',
        formLabel: 'Billing Requirements',
        formDescription: 'Capture preferences related to billing, such as use of department codes, outsourcing arrangements, or other special requirements.',
        placeholder: 'e.g., Specific department codes required for cost allocation, outsourced billing to third party, multi-entity billing split, consolidated invoicing preferences',
        pdfLabel: 'Billing requirements',
        pdfDescription: 'Billing preferences including department codes, outsourcing arrangements, and special requirements.',
        contributesToCompletion: false,
      },
      {
        id: 'general_portal_access_users_summary',
        tabId: 'general',
        sectionId: 'general_pdf_export',
        groupId: 'general_portal_access_users',
        path: 'generalInfo.portalAccessUsers',
        valueType: 'json',
        formLabel: 'Portal access users',
        formDescription: 'All users listed here will be added as Administrators and will be able to add other users and assign roles.',
        pdfLabel: 'Portal access users',
        pdfDescription: 'List of portal access users who will be granted admin access.',
        contributesToCompletion: false,
      },
      {
        id: 'general_custom_works',
        tabId: 'custom_work',
        sectionId: 'general_pdf_export',
        groupId: 'general_custom_works_group',
        path: 'generalInfo.customWorks',
        valueType: 'textarea',
        formLabel: 'Custom Works',
        pdfLabel: 'Custom Works',
        pdfDescription: 'Included for transparency on agreed non-standard changes.',
        contributesToCompletion: false,
      },
      {
        id: 'general_production_access_date',
        tabId: 'general',
        sectionId: 'general_pdf_export',
        groupId: 'general_timeline_volume',
        path: 'generalInfo.productionAccessDate',
        valueType: 'text',
        formLabel: 'Production access date',
        formDescription: 'Required for completion. Target date to receive FrankieOne Production keys.',
        pdfLabel: 'Production access date',
        pdfDescription: 'Provide a target date for production keys.',
        contributesToCompletion: false,
      },
      {
        id: 'general_go_live_date',
        tabId: 'general',
        sectionId: 'general_pdf_export',
        groupId: 'general_timeline_volume',
        path: 'generalInfo.goLiveDate',
        valueType: 'text',
        formLabel: 'Expected go-live date',
        formDescription: 'Required for completion. Target date the client expects to go live.',
        pdfLabel: 'Expected go-live date',
        pdfDescription: 'Provide the expected go-live date.',
        contributesToCompletion: false,
      },
      {
        id: 'general_is_hard_go_live_date',
        tabId: 'general',
        sectionId: 'general_pdf_export',
        groupId: 'general_timeline_volume',
        path: 'generalInfo.isHardGoLiveDate',
        valueType: 'text',
        formLabel: 'Hard go-live date',
        formDescription: 'Whether the go-live date is a firm commitment.',
        pdfLabel: 'Hard go-live date',
        pdfDescription: 'Indicates if the go-live date is a firm commitment vs. tentative.',
        contributesToCompletion: false,
      },
      {
        id: 'general_forecast_checks_12m',
        tabId: 'general',
        sectionId: 'general_pdf_export',
        groupId: 'general_timeline_volume',
        path: 'generalInfo.forecastChecks12m',
        valueType: 'number',
        formLabel: 'Forecast KYC/AML checks (12 months)',
        formDescription: 'Estimated number of checks expected in the next 12 months.',
        pdfLabel: 'Forecast checks (12 months)',
        pdfDescription: 'Estimated number of checks expected in the next 12 months.',
        contributesToCompletion: false,
      },
      {
        id: 'general_parent_child',
        tabId: 'advanced',
        sectionId: 'general_pdf_export',
        groupId: 'general_corporate_structure',
        path: 'generalInfo.parentChild',
        valueType: 'select',
        options: [
          { value: 'unknown', label: "Don't Know" },
          { value: 'parent_only', label: 'Parent Only' },
          { value: 'parent_child', label: 'Parent + Child' },
          { value: 'child', label: 'Child Only' },
        ],
        formLabel: 'Parent / Child relationship',
        formDescription:
          'Required for completion. Parent Only = standalone parent account. Parent + Child = parent with nested child. Child Only = child account under an existing parent (e.g. reseller children).',
        pdfLabel: 'Parent / Child relationship',
        pdfDescription: 'Select whether the client will use Parent only, Parent + Child, or Child only accounts.',
        contributesToCompletion: false,
      },
      {
        id: 'general_parent_account_name',
        tabId: 'advanced',
        sectionId: 'general_pdf_export',
        groupId: 'general_corporate_structure',
        path: 'generalInfo.parentAccountName',
        valueType: 'text',
        formLabel: 'Parent account name',
        formDescription: 'Optional: FrankieOne account name for the parent.',
        pdfLabel: 'Parent account name',
        pdfDescription: 'Parent account name (if applicable).',
        contributesToCompletion: false,
      },
      {
        id: 'general_parent_uat_id',
        tabId: 'advanced',
        sectionId: 'general_pdf_export',
        groupId: 'general_corporate_structure',
        path: 'generalInfo.parentUatId',
        valueType: 'text',
        formLabel: 'Parent UAT ID',
        formDescription: 'Optional: UAT environment account identifier.',
        pdfLabel: 'Parent UAT ID',
        pdfDescription: 'Parent UAT environment account identifier (if applicable).',
        contributesToCompletion: false,
      },
      {
        id: 'general_parent_prod_id',
        tabId: 'advanced',
        sectionId: 'general_pdf_export',
        groupId: 'general_corporate_structure',
        path: 'generalInfo.parentProdId',
        valueType: 'text',
        formLabel: 'Parent production ID',
        formDescription: 'Optional: Production environment account identifier.',
        pdfLabel: 'Parent production ID',
        pdfDescription: 'Parent production environment account identifier (if applicable).',
        contributesToCompletion: false,
      },
      {
        id: 'general_child_account_name',
        tabId: 'advanced',
        sectionId: 'general_pdf_export',
        groupId: 'general_corporate_structure',
        path: 'generalInfo.childAccountName',
        valueType: 'text',
        formLabel: 'Child account name',
        formDescription: 'Optional: FrankieOne account name for the child.',
        pdfLabel: 'Child account name',
        pdfDescription: 'Child account name (if applicable).',
        contributesToCompletion: false,
      },
      {
        id: 'general_child_uat_id',
        tabId: 'advanced',
        sectionId: 'general_pdf_export',
        groupId: 'general_corporate_structure',
        path: 'generalInfo.childUatId',
        valueType: 'text',
        formLabel: 'Child UAT ID',
        formDescription: 'Optional: UAT environment account identifier.',
        pdfLabel: 'Child UAT ID',
        pdfDescription: 'Child UAT environment account identifier (if applicable).',
        contributesToCompletion: false,
      },
      {
        id: 'general_child_prod_id',
        tabId: 'advanced',
        sectionId: 'general_pdf_export',
        groupId: 'general_corporate_structure',
        path: 'generalInfo.childProdId',
        valueType: 'text',
        formLabel: 'Child production ID',
        formDescription: 'Optional: Production environment account identifier.',
        pdfLabel: 'Child production ID',
        pdfDescription: 'Child production environment account identifier (if applicable).',
        contributesToCompletion: false,
      },
      {
        id: 'general_integration_type',
        tabId: 'general',
        sectionId: 'general_pdf_export',
        groupId: 'general_technical_setup',
        path: 'generalInfo.integrationType',
        valueType: 'select',
        formLabel: 'Integration type',
        formDescription: 'Required for completion. How the client will integrate with FrankieOne (API, Portal, or both).',
        pdfLabel: 'Integration type',
        pdfDescription: 'Select whether the client integrates via API, Portal, or both.',
        contributesToCompletion: false,
      },
      {
        id: 'general_platform_version',
        tabId: 'general',
        sectionId: 'general_pdf_export',
        groupId: 'general_technical_setup',
        path: 'generalInfo.platformVersion',
        valueType: 'select',
        formLabel: 'Platform version',
        formDescription: 'Which FrankieOne platform version will be used.',
        pdfLabel: 'Platform version',
        pdfDescription: 'Confirm which FrankieOne platform version will be used.',
        contributesToCompletion: false,
      },
      {
        id: 'general_sso_access',
        tabId: 'general',
        sectionId: 'general_pdf_export',
        groupId: 'general_technical_setup',
        path: 'generalInfo.ssoAccess',
        valueType: 'tri_state',
        formLabel: 'SSO access required',
        formDescription: 'Required for completion. Whether the client requires SSO access for the FrankieOne portal.',
        pdfLabel: 'SSO access required',
        pdfDescription: 'Confirm whether SSO is required for portal access.',
        contributesToCompletion: false,
      },
      {
        id: 'general_ai_features',
        tabId: 'general',
        sectionId: 'general_pdf_export',
        groupId: 'general_technical_setup',
        path: 'generalInfo.aiFeatures',
        valueType: 'tri_state',
        formLabel: 'AI features',
        formDescription: 'Required for completion. Whether AI features should be disabled for this account.',
        pdfLabel: 'AI features',
        pdfDescription: 'Confirm whether AI features should be disabled for this account.',
        contributesToCompletion: false,
      },
      {
        id: 'general_phases',
        tabId: 'general',
        sectionId: 'general_pdf_export',
        groupId: 'general_phases_group',
        path: 'generalInfo.phases',
        valueType: 'textarea',
        formLabel: 'Phases',
        formDescription: 'Required for completion. Up to 500 words per phase.',
        pdfLabel: 'Phases',
        pdfDescription: 'Add phase scope descriptions (each phase should be filled in).',
        contributesToCompletion: false,
      },
      {
        id: 'general_sow_variation_note',
        tabId: 'advanced',
        sectionId: 'general_pdf_export',
        groupId: 'general_sow_variation_group',
        path: 'generalInfo.sowVariationNote',
        valueType: 'textarea',
        formLabel: 'SOW variation note',
        pdfLabel: 'SOW variation note',
        pdfDescription: 'Variation note (if applicable).',
        contributesToCompletion: false,
      },
      {
        id: 'general_testing_requirements',
        tabId: 'advanced',
        sectionId: 'general_pdf_export',
        groupId: 'general_testing_group',
        path: 'generalInfo.testDetails',
        valueType: 'textarea',
        formLabel: 'Testing requirements',
        pdfLabel: 'Testing requirements',
        pdfDescription: 'Capture specific UAT testing requirements and connector preferences.',
        contributesToCompletion: false,
      },
      {
        id: 'workflow_cascade',
        tabId: 'general',
        sectionId: 'general_pdf_export',
        path: 'workflowsConfig',
        valueType: 'text',
        formLabel: 'Cascade',
        pdfLabel: 'Cascade',
        pdfDescription: 'Cascade sources configuration summary.',
        contributesToCompletion: false,
      },
      {
        id: 'general_customer_name',
        tabId: 'general',
        sectionId: 'general_pdf_export',
        path: 'customerName',
        valueType: 'text',
        formLabel: 'Customer name',
        pdfLabel: 'Customer name',
        pdfDescription: 'Enter the legal customer name used for onboarding.',
        contributesToCompletion: false,
      },
      {
        id: 'general_customer_abn',
        tabId: 'general',
        sectionId: 'general_pdf_export',
        path: 'customerAbn',
        valueType: 'text',
        formLabel: 'ABN',
        formDescription: 'Australian Business Number. When set, displays next to the Client name in the SOW legal text.',
        pdfLabel: 'ABN',
        pdfDescription: 'Customer ABN (if applicable).',
        contributesToCompletion: false,
      },
      {
        id: 'general_pdf_document_title',
        tabId: 'general',
        sectionId: 'general_pdf_export',
        path: 'customerName',
        valueType: 'text',
        formLabel: 'PDF document title',
        pdfLabel: 'Scope Configuration Summary',
        pdfDescription: 'PDF document title.',
        contributesToCompletion: false,
      },
      {
        id: 'general_pdf_document_intro',
        tabId: 'general',
        sectionId: 'general_pdf_export',
        path: 'customerName',
        valueType: 'text',
        formLabel: 'PDF document intro',
        pdfLabel: 'PDF intro',
        pdfDescription: 'This document summarises the choices made so far, and highlights decisions that still need to be made.',
        contributesToCompletion: false,
      },
      {
        id: 'general_pdf_pending_legend',
        tabId: 'general',
        sectionId: 'general_pdf_export',
        path: 'customerName',
        valueType: 'text',
        formLabel: 'PDF pending decisions legend',
        pdfLabel: 'PDF legend',
        pdfDescription: 'Highlighted items indicate a decision is still required (empty / unknown / tbd).',
        contributesToCompletion: false,
      },
    ],
  },
]
